<script lang="ts" setup>
import blueprintLogo from '~/assets/icons/press/blueprint_new.svg?url';
import buroLogo from '~/assets/icons/press/buro_new2.svg?url';
import forbesLogo from '~/assets/icons/press/forbes_new.svg?url';
import rbcLogo from '~/assets/icons/press/rbc_new.svg?url';
import umagLogo from '~/assets/icons/press/umag_new3.svg?url';

type PressArticle = {
  id: string;
  title: string;
  url: string;
  logo: string;
  alt: string;
};

const PRESS_LOGOS: Record<string, string> = {
  rbc: rbcLogo,
  buro: buroLogo,
  blueprint: blueprintLogo,
  umag: umagLogo,
  forbes: forbesLogo,
};

const { t, tm: tm2, rt } = useI18n();
const tm = (key: string) => tm2(key) as any[];

const articles = computed<PressArticle[]>(() => {
  const rawArticles = tm('articles');

  return rawArticles.map((article) => {
    const logoKey = rt(article.logo);
    const logo = PRESS_LOGOS[logoKey];

    if (!logo) throw new Error(`unknown logo source for ${logoKey}`);

    const title = rt(article.title);

    return {
      id: logoKey,
      title,
      url: rt(article.url),
      logo,
      alt: article.alt ? rt(article.alt) : title,
    };
  });
});
</script>

<template>
  <section class="pressMentions">
    <h2 class="pressMentions__title">
      {{ t('title') }}
    </h2>

    <BaseControlledSlider
      :list="articles"
      :slides-per-view="'auto'"
      :space-between="{ mobile: 23, desktop: 50 }"
      slider-class="pressMentions__slider"
    >
      <template #slide="{ item }">
        <NuxtLink :to="item.url" :title="item.title" class="pressMentions__logoLink" target="_blank" external>
          <img :src="item.logo" :alt="item.alt" class="pressMentions__logo" />
        </NuxtLink>
      </template>
    </BaseControlledSlider>
  </section>
</template>

<i18n>
ru:
  title: СМИ о нас
  articles:
    - title: Основательница Don't Touch My Skin — о том, как запустить бьюти-бренд
      url: 'https://style.rbc.ru/people/6729eed49a7947bc823e1fba'
      logo: rbc
    - alt: Forbes Woman
      title: 'Жизнь Адэль: как геолог из башкирского села стала одним из самых популярных бьюти-блогеров России'
      url: https://www.forbes.ru/forbes-woman/389391-zhizn-adel-kak-geolog-iz-bashkirskogo-sela-stala-odnim-iz-samyh-populyarnyh
      logo: forbes
    - title: История (жизни) Банки Don't Touch My Skin
      url: https://theblueprint.ru/beauty/industry/dont-touch-my-skin-sergi
      logo: blueprint
    - title: Don't Touch My Skin и Blizhe выпустили совместную коллекцию купальников
      url: https://www.buro247.ru/news/style/17-aug-2022-dont-touch-my-skin-blizhe-collection.html
      logo: buro
    - title: Самые интересные бьюти-запуски недели, на которые стоит обратить внимание
      url: 'https://umagazine.ru/krasota/reviews/samye-interesnye-byuti-zapuski-nedeli-na-kotorye-stoit-obratit-vnimanie-lichnyy-skincare-assistent-i/'
      logo: umag

en:
  title: Press about us
  articles:
    - title: The founder of Don't Touch My Skin on launching a beauty brand
      url: https://style.rbc.ru/people/6729eed49a9747bc823e1fba
      logo: rbc
    - title: 'The life of Adel: from a Bashkir village geologist to a top beauty blogger'
      url: https://www.forbes.ru/forbes-woman/389391-zhizn-adel-kak-geolog-iz-bashkirskogo-sela-stala-odnim-iz-samyh-populyarnyh
      logo: forbes
    - title: The story behind Don't Touch My Skin
      url: https://theblueprint.ru/beauty/industry/dont-touch-my-skin-sergi
      logo: blueprint
    - title: Don't Touch My Skin and Blizhe released a swimwear capsule
      url: https://www.buro247.ru/news/style/17-aug-2022-dont-touch-my-skin-blizhe-collection.html
      logo: buro
    - title: Beauty launches worth attention this week
      url: 'https://umagazine.ru/krasota/reviews/samye-interesnye-byuti-zapuski-nedeli-na-kotorye-stoit-obratit-vnimanie-lichnyy-skincare-assistent-i/'
      logo: umag
</i18n>

<style lang="scss">
.pressMentions {
  margin-top: 40px;
  padding: 40px 20px 100px;

  @include mq('sm') {
    padding: 16px 16px 72px;
  }

  &__title {
    margin: 0 0 20px;
    font-size: 34px;
    line-height: 40px;
    letter-spacing: 0.2px;
    color: #121212;
    text-align: center;

    @include mq('sm') {
      margin-bottom: 16px;
      font-size: 24px;
      line-height: 32px;
      text-align: left;
    }
  }

  // Высота строки фиксирована (Figma 61px), ширина слайда = естественная
  // ширина логотипа. width: auto перекрывает дефолтный .swiper-slide
  // { width: 100% }, иначе Swiper 'auto' показывает по 1 лого на экран.
  &__slider .swiper-slide {
    width: auto;
    height: 61px;
    display: flex;
    align-items: center;

    @include mq('sm') {
      height: 28px;
    }
  }

  &__logoLink {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    color: #121212;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.6;
    }

    &:focus-visible {
      outline: 2px solid #8ab844;
      outline-offset: 4px;
    }
  }

  // Высота фикс, ширина — auto: считается из intrinsic-размера SVG после
  // загрузки (Swiper пересчитает слайды через updateOnImagesReady).
  &__logo {
    height: 61px;
    width: auto;
    max-width: none;
    display: block;

    @include mq('sm') {
      height: 28px;
    }
  }

  // Пагинация-точки для ленты логотипов не нужны.
  &__slider .swiper-pagination {
    display: none;
  }

  // Когда все лого помещаются без скролла (isStart && isEnd) — центрируем
  // ленту. На переполнении класс снимается, остаётся flex-start для скролла.
  .baseControlledSlider_fit .swiper-wrapper {
    justify-content: center;
  }
}
</style>
